/* eslint-disable @typescript-eslint/naming-convention */
import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import queryString from "query-string";
import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { filterFactory } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import type { IChosenItemParams } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { useSearchAdvertisingDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useSearchAdvertisingDataAndParams";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useWebsiteKeywordsEndpoints } from "../websiteKeywordsEndpoints";
import { useWebsiteKeywordsPage2Context } from "../WebsiteKeywordsPage2Context";

import type {
    PresetWithDisabled,
    EPresetOption,
} from "app/common/keyword-research/website-keywords/competitiveFilterPresets";
import { usePresetsBoxesConfig } from "app/common/keyword-research/website-keywords/competitiveFilterPresets";

const fetchService = DefaultFetchService.getInstance();

interface ITrafficPresetFilterState {
    trafficPreset?: EPresetOption;
}

const Filter = filterFactory.createTypedFilters<ITrafficPresetFilterState>();

const PresetsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 8px;
    width: 100%;
    background-color: ${colorsPalettes.carbon[0]};
`;

export const WebsiteKeywordsPage2CompetitiveFilter = () => {
    const { requestBody, apiParams } = useWebsiteKeywordsPage2Context();
    const { translate } = useTranslate();
    const { pageFilter, key, selectedPageTab = "", trafficPreset, country } = useSearchAdvertisingDataAndParams();
    const endpoints = useWebsiteKeywordsEndpoints();
    const chosenItems: IChosenItemParams = safeJsonParse(pageFilter, []) || [];
    const splitKeys: string[] = pageFilter ? [] : key.split(",");
    const mainKey = chosenItems[0]?.url ?? splitKeys[0] ?? "";
    const isCompare = (pageFilter ? chosenItems.length : splitKeys.length) > 1;
    const isWorldWide = country === 999;

    const filtersForRequest = useMemo(() => {
        const next = Object.fromEntries(Object.entries(apiParams).filter(([, value]) => value != null));
        delete next["Preset"];
        delete next["sort"];
        delete next["asc"];
        return next;
    }, [apiParams]);

    const { data, isLoading, isError } = useQuery({
        queryKey: ["website-keywords", "presets-total", endpoints.presetsTotal, filtersForRequest, requestBody],
        queryFn: () =>
            fetchService.post<{
                TotalPresets: Record<EPresetOption, number>;
                Total: number;
            }>(`${endpoints.presetsTotal}?${queryString.stringify(filtersForRequest)}`, requestBody),
    });

    const counts = data?.TotalPresets ?? ({} as Partial<Record<EPresetOption, number>>);

    const presetBoxes = usePresetsBoxesConfig(mainKey, isCompare, selectedPageTab, isWorldWide);

    const handleChange = (newState: ITrafficPresetFilterState) => {
        // Preserve toggle-on-click UX: clicking the active card deselects it.
        const nextPreset = newState.trafficPreset === trafficPreset ? undefined : newState.trafficPreset;

        if (nextPreset) {
            allTrackers.dispatchTrackEvent("Button", "click", `Select Competitive Traffic Preset/${nextPreset}`);
        } else if (trafficPreset) {
            allTrackers.dispatchTrackEvent("Button", "click", `Deselect Competitive Traffic Preset/${trafficPreset}`);
        }

        void proNavigator.updateParams({ trafficPreset: nextPreset });
    };

    const dataLabel = translate("website.keywords.2.0.page.table.overview.competitive.filter.preset.dataLabel");

    const fieldValue = (preset: PresetWithDisabled<EPresetOption>) => {
        if (isError) {
            return undefined;
        }
        if (preset.disabled) {
            return 0;
        }
        const count = counts[preset.id] ?? 0;
        return count.toLocaleString();
    };

    return (
        <Filter.Provider
            state={{ trafficPreset: trafficPreset as EPresetOption }}
            onChange={handleChange}
        >
            <PresetsGrid data-automation="website-keywords-competitive-presets">
                {presetBoxes.map((preset) => (
                    <Filter.QuickFilter
                        key={preset.id}
                        title={preset.title}
                        tooltip={preset.tooltipContent}
                        isLoading={isLoading}
                        isDisabled={preset.disabled}
                        fieldText={fieldValue(preset)}
                        fieldLabel={dataLabel}
                        value={{ trafficPreset: preset.id }}
                    />
                ))}
            </PresetsGrid>
        </Filter.Provider>
    );
};
